import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const AdminCategories = () => {
  // --- State Management ---
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  // API URL
  const API_URL = `${process.env.REACT_APP_BACKEND}/api/categories`;

  // --- Lifecycle Methods ---
  useEffect(() => {
    fetchCategories();
  }, []);

  // --- API Calls ---
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL);
      setCategories(response.data);
      setError("");
    } catch (err) {
      setError(
        "Failed to fetch categories. Please ensure the server is running.",
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this category? Products in this category may be affected.",
      )
    )
      return;

    try {
      await axios.delete(`${API_URL}/${id}`);
      fetchCategories();
    } catch (err) {
      setError("Failed to delete category.");
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, formData);
      } else {
        await axios.post(API_URL, formData);
      }
      resetForm();
      fetchCategories();
    } catch (err) {
      setError("Failed to save category.");
      console.error(err);
    }
  };

  // --- Helper Methods ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEdit = (category) => {
    setEditId(category.id);
    setFormData({
      name: category.name,
      description: category.description || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
    });
    setEditId(null);
    setShowForm(false);
    setError("");
  };

  // Search filtering logic
  const filteredCategories = categories.filter(
    (cat) =>
      cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cat.description &&
        cat.description.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  return (
    <div
      className="container-fluid px-4 bg-light min-vh-100"
      style={{ paddingTop: "80px", paddingBottom: "40px" }}
    >
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h2 className="fw-bold mb-0 text-dark">Category Management</h2>
          <span className="text-muted small">
            Organize and manage your beeLxe product categories
          </span>
        </div>
        <div className="d-flex gap-2">
          <button
            className={`btn px-4 py-2 fw-semibold shadow-sm ${
              showForm ? "btn-secondary" : "btn-primary"
            }`}
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
          >
            {showForm ? (
              <>
                <i className="bi bi-x-circle me-2"></i>Cancel
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-2"></i>Add New Category
              </>
            )}
          </button>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show shadow-sm border-0 mb-4"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      {/* Category Form Card */}
      {showForm && (
        <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="mb-0 fw-bold text-primary">
              {editId ? "Edit Category" : "Create New Category"}
            </h5>
          </div>
          <div className="card-body p-4 bg-white">
            <form onSubmit={handleSubmit}>
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg bg-light border-0"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Raw Honey, Beeswax Candling..."
                    required
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Description
                  </label>
                  <textarea
                    className="form-control bg-light border-0"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief overview of products inside this category..."
                  ></textarea>
                </div>
              </div>
              <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                <button
                  type="submit"
                  className="btn btn-success btn-lg px-5 shadow-sm fw-bold"
                >
                  {editId ? "Update Category" : "Save Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Control Bar: Search */}
      <div className="card shadow-sm border-0 rounded-4 mb-4">
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            <div className="col-md-6 col-lg-4">
              <div className="input-group">
                <span className="input-group-text bg-light border-0 text-muted">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control bg-light border-0"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6 col-lg-8 text-end text-muted small">
              Total Categories: <strong>{categories.length}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table Section */}
      <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th
                  className="text-muted small text-uppercase fw-semibold py-3 ps-4"
                  style={{ width: "80px" }}
                >
                  ID
                </th>
                <th className="text-muted small text-uppercase fw-semibold py-3">
                  Category Name
                </th>
                <th className="text-muted small text-uppercase fw-semibold py-3">
                  Description
                </th>
                <th className="text-muted small text-uppercase fw-semibold py-3 text-end pe-4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-5 text-muted">
                    <i className="bi bi-folder2-open fs-1 d-block mb-2"></i>
                    {searchTerm
                      ? "No categories match your search query."
                      : "No categories found. Click 'Add New Category' to create one."}
                  </td>
                </tr>
              ) : (
                filteredCategories.map((category) => (
                  <tr key={category.id}>
                    <td className="ps-4 fw-bold text-secondary">
                      #{category.id}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-primary bg-opacity-10 rounded-circle me-3 text-primary d-flex align-items-center justify-content-center"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <i className="bi bi-tag-fill"></i>
                        </div>
                        <span className="fw-bold text-dark">
                          {category.name}
                        </span>
                      </div>
                    </td>
                    <td className="text-muted small pe-4">
                      {category.description || (
                        <em className="text-black-50">
                          No description provided
                        </em>
                      )}
                    </td>
                    <td className="text-end pe-4">
                      <button
                        className="btn btn-sm btn-light text-primary rounded-circle me-2 shadow-sm"
                        onClick={() => handleEdit(category)}
                        title="Edit Category"
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-light text-danger rounded-circle shadow-sm"
                        onClick={() => handleDelete(category.id)}
                        title="Delete Category"
                      >
                        <i className="bi bi-trash3"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <button className="btn btn-primary">
            <Link to="/products" className="text-white text-decoration-none">
              Back to Products
            </Link>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
